import type { ElementType } from "react";
import { Button as BaseButton } from "@base-ui/react/button";
import { IconArrowRight, IconPlus } from "@tabler/icons-react";
import Card from "../UI/Card";
import Status from "../UI/Status";
import Text, { type Color } from "../UI/Text";
import type { DiagnosticStatusCode } from "../../utils/documentStatus";
// @ts-expect-error pure js
import { classnames } from "../../utils";
import styles from "./DiagnosticsKpis.module.css";

export interface DiagnosticsCounts {
  /** Diagnostics attendus sur les biens affichés (hors « non concerné »). */
  required: number;
  /** Diagnostics en cours de validité, propres ou hérités. */
  valide: number;
  manquant: number;
  expire: number;
  bientotExpire: number;
}

interface DiagnosticsKpisProps {
  counts: DiagnosticsCounts;
  activeStatus: string[];
  onToggleStatus: (status: DiagnosticStatusCode) => void;
}

const RING_SIZE = 56;
const RING_STROKE = 6;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

function ConformityRing({ ratio }: { ratio: number }) {
  const percent = Math.round(ratio * 100);
  const severity = ratio >= 0.8 ? "good" : ratio >= 0.5 ? "warning" : "danger";
  return (
    <div className={styles.ring} role="img" aria-label={`${percent} %`}>
      <svg width={RING_SIZE} height={RING_SIZE} aria-hidden="true">
        <circle
          className={styles.ringTrack}
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          strokeWidth={RING_STROKE}
        />
        <circle
          className={classnames(styles.ringFill, styles[severity])}
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          strokeWidth={RING_STROKE}
          strokeDasharray={RING_LENGTH}
          strokeDashoffset={RING_LENGTH * (1 - ratio)}
        />
      </svg>
      <Text as="span" variant="deci" weight="bold" className={styles.ringLabel}>
        {percent}%
      </Text>
    </div>
  );
}

interface TileProps {
  label: string;
  color: Color;
  icon?: ElementType;
  value: number;
  caption: string;
  active: boolean;
  onClick: () => void;
}

function Tile({
  label,
  color,
  icon,
  value,
  caption,
  active,
  onClick,
}: TileProps) {
  return (
    <Card
      as={BaseButton}
      onClick={onClick}
      checked={active}
      padding="sm"
      className={styles.tile}
      aria-pressed={active}
    >
      <div className={styles.tileHeader}>
        <Status as="span" color={color} icon={icon} text={label} />
        <IconArrowRight size={18} stroke={1.5} className={styles.arrow} />
      </div>
      <div className={styles.tileValue}>
        <Text as="span" variant="mega" weight="bold">
          {value}
        </Text>
        <Text as="span" color="muted">
          {caption}
        </Text>
      </div>
    </Card>
  );
}

/** Tuiles de synthèse de l'onglet Diagnostics ; un clic sur une tuile filtre le tableau sur ce statut. */
export default function DiagnosticsKpis({
  counts,
  activeStatus,
  onToggleStatus,
}: DiagnosticsKpisProps) {
  const isActive = (status: DiagnosticStatusCode) =>
    activeStatus.length === 1 && activeStatus[0] === status;
  const ratio = counts.required > 0 ? counts.valide / counts.required : 1;
  return (
    <div className={styles.row}>
      <Card padding="sm" className={styles.conformity}>
        <ConformityRing ratio={ratio} />
        <div className={styles.conformityText}>
          <Text as="span" color="muted">
            Conformité du parc
          </Text>
          <Text as="span" weight="bold">
            {counts.valide} sur {counts.required} diagnostic
            {counts.required > 1 ? "s" : ""} à jour
          </Text>
        </div>
      </Card>
      <Tile
        label="À ajouter"
        color="neutral"
        icon={IconPlus}
        value={counts.manquant}
        caption={`document${counts.manquant > 1 ? "s" : ""} attendu${
          counts.manquant > 1 ? "s" : ""
        }`}
        active={isActive("manquant")}
        onClick={() => onToggleStatus("manquant")}
      />
      <Tile
        label={counts.expire > 1 ? "Expirés" : "Expiré"}
        color="danger"
        value={counts.expire}
        caption="à renouveler"
        active={isActive("expire")}
        onClick={() => onToggleStatus("expire")}
      />
      <Tile
        label={counts.bientotExpire > 1 ? "Expirent bientôt" : "Expire bientôt"}
        color="warning"
        value={counts.bientotExpire}
        caption="sous 2 mois"
        active={isActive("bientotExpire")}
        onClick={() => onToggleStatus("bientotExpire")}
      />
    </div>
  );
}
